import { useNavigate } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useCheckout, useTiers, type Tier } from '../lib/api'

const FALLBACK: Tier[] = [
  { code: 'starter', name: 'Starter', fee: '49',  startingBalance: '5000',   profitTargetP1Pct: '8', profitTargetP2Pct: '5', profitSplitPct: '70', description: 'Entry-level — low stakes, low buy-in.' },
  { code: 'pro',     name: 'Pro',     fee: '149', startingBalance: '25000',  profitTargetP1Pct: '8', profitTargetP2Pct: '5', profitSplitPct: '80', description: 'The most popular tier. Larger capital, bigger split.' },
  { code: 'elite',   name: 'Elite',   fee: '399', startingBalance: '100000', profitTargetP1Pct: '8', profitTargetP2Pct: '5', profitSplitPct: '90', description: 'For experienced traders. Full funded scale.' },
]

function fmtEur0(v: string | number): string {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

export function OnboardingPage() {
  const tiers = useTiers()
  const checkout = useCheckout()
  const nav = useNavigate()

  const items: Tier[] = tiers.data && tiers.data.length > 0 ? tiers.data : FALLBACK

  const onPick = (code: string) => {
    checkout.mutate(
      { tierCode: code, isReset: false },
      {
        onSuccess: (res) => {
          if (res.checkoutUrl?.startsWith('http')) {
            window.location.href = res.checkoutUrl
          } else {
            nav(`/mock/stripe/checkout/${res.sessionId}?tier=${code}`)
          }
        },
      },
    )
  }

  return (
    <AppShell page="challenge" showBalance={false} breadcrumb={<span className="topbar__breadcrumb-current">Pick a tier</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Get started</span></p>
          <h1 className="page-header__title">Pick your challenge</h1>
          <p className="page-header__subtitle">
            Three tiers, same four-phase flow. Pick the size you're comfortable with — you can always start another later.
          </p>
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
        {items.map((t, idx) => {
          const featured = t.code === 'pro' || (idx === 1 && items.length === 3)
          return (
            <article key={t.code} className="settings-card"
              style={featured ? { borderColor: 'var(--color-primary)', boxShadow: '0 0 0 2px var(--color-primary-subtle), var(--shadow-md)' } : undefined}>
              <header className="settings-card__header" style={{ paddingBottom: 'var(--space-3)' }}>
                <div className="settings-card__title-group" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <h2 className="settings-card__title">{t.name}</h2>
                  {featured && (
                    <span className="chip chip--won">
                      <span className="chip__dot" aria-hidden />
                      Most popular
                    </span>
                  )}
                </div>
              </header>
              <div className="settings-card__body">
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-3xl)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  {fmtEur0(t.fee)}
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
                  Starting capital{' '}
                  <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{fmtEur0(t.startingBalance)}</strong>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
                  fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
                  <li>Phase 1 target · {t.profitTargetP1Pct}%</li>
                  <li>Phase 2 target · {t.profitTargetP2Pct}%</li>
                  <li>Profit split · {t.profitSplitPct}%</li>
                  {t.description && (
                    <li style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                      {t.description}
                    </li>
                  )}
                </ul>
                <button type="button" onClick={() => onPick(t.code)} disabled={checkout.isPending}
                  className={featured ? 'btn btn--primary' : 'btn btn--secondary'}
                  style={{ width: '100%', justifyContent: 'center' }}>
                  {checkout.isPending ? 'Loading…' : 'Choose this tier'}
                </button>
              </div>
            </article>
          )
        })}
      </div>

      {checkout.isError && (
        <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
          {apiErrorMessage(checkout.error)}
        </div>
      )}
    </AppShell>
  )
}
